import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CosmicBackground } from "../components/CosmicBackground";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS = Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i);

export default function Compatibility() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    partner_name: "",
    birth_month: "",
    birth_day: "",
    birth_year: "",
    birth_time: "12:00",
    birth_city: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        partner_name: form.partner_name,
        birth_month: parseInt(form.birth_month),
        birth_day: parseInt(form.birth_day),
        birth_year: parseInt(form.birth_year),
        birth_time: form.birth_time,
        birth_city: form.birth_city,
      };
      const { data } = await api.post("/compatibility", payload);
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403) {
        toast.error("Upgrade to Quester to unlock compatibility");
        setTimeout(() => navigate("/pricing"), 1500);
      } else {
        toast.error(detail || "Could not generate report");
      }
    } finally {
      setLoading(false);
    }
  };

  const ready = form.partner_name && form.birth_month && form.birth_day && form.birth_year && form.birth_city;

  return (
    <CosmicBackground>
      <header className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
        <button onClick={() => navigate("/dashboard")} className="text-[#94A3B8] hover:text-white text-sm flex items-center gap-1" data-testid="compat-back-btn">
          <ArrowLeft size={14} /> Dashboard
        </button>
        <span className="font-serif-mystic text-xl tracking-wide">AuraQuest</span>
      </header>
      <main className="max-w-3xl mx-auto px-6 md:px-12 pb-24" data-testid="compatibility-page">
        <div className="text-center mb-10">
          <Heart size={28} className="text-[#D4AF37] mx-auto" strokeWidth={1.5} />
          <h1 className="font-serif-mystic text-4xl sm:text-5xl tracking-tight mt-2">Compatibility</h1>
          <p className="text-[#94A3B8] mt-2">Enter your partner&apos;s birth data to receive a written reading from the cosmos.</p>
        </div>

        {!result ? (
          <div className="rounded-2xl border border-white/10 bg-[#111625]/80 backdrop-blur-xl p-6 sm:p-8 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Partner&apos;s name</Label>
              <Input value={form.partner_name} onChange={(e) => setForm({ ...form, partner_name: e.target.value })} className="mt-2 bg-[#05070A]/60 border-white/10 text-white" data-testid="partner-name" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Month</Label>
                <Select value={form.birth_month} onValueChange={(v) => setForm({ ...form, birth_month: v })}>
                  <SelectTrigger className="mt-2 bg-[#05070A]/60 border-white/10 text-white" data-testid="partner-month"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0E17] text-white border-white/10 max-h-72">
                    {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Day</Label>
                <Select value={form.birth_day} onValueChange={(v) => setForm({ ...form, birth_day: v })}>
                  <SelectTrigger className="mt-2 bg-[#05070A]/60 border-white/10 text-white" data-testid="partner-day"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0E17] text-white border-white/10 max-h-72">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Year</Label>
                <Select value={form.birth_year} onValueChange={(v) => setForm({ ...form, birth_year: v })}>
                  <SelectTrigger className="mt-2 bg-[#05070A]/60 border-white/10 text-white" data-testid="partner-year"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#0A0E17] text-white border-white/10 max-h-72">
                    {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Birth time</Label>
              <Input type="time" value={form.birth_time} onChange={(e) => setForm({ ...form, birth_time: e.target.value })} className="mt-2 bg-[#05070A]/60 border-white/10 text-white" data-testid="partner-time" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-[#94A3B8]">Birth city</Label>
              <Input value={form.birth_city} onChange={(e) => setForm({ ...form, birth_city: e.target.value })} placeholder="Paris, France" className="mt-2 bg-[#05070A]/60 border-white/10 text-white" data-testid="partner-city" />
            </div>
            <Button onClick={submit} disabled={!ready || loading} data-testid="compat-submit-btn" className="w-full bg-[#D4AF37] text-[#05070A] hover:bg-[#F3E5AB]">
              {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Consulting the cosmos…</> : "Reveal Reading"}
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#111625]/80 backdrop-blur-xl p-8" data-testid="compat-report">
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Reading</p>
            <h2 className="font-serif-mystic text-3xl mb-1">You & {result.partner_name}</h2>
            {result.partner_hd && (
              <p className="text-sm text-[#94A3B8] mb-3">
                {result.partner_name} is a <span className="text-white">{result.partner_hd.type}</span> · Profile {result.partner_hd.profile}
              </p>
            )}
            {result.synastry?.match_percentage != null && (
              <div className="mb-5 flex items-center gap-3" data-testid="compat-score">
                <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">Match</span>
                <span className="font-serif-mystic text-3xl gold-shimmer">{result.synastry.match_percentage}%</span>
              </div>
            )}
            <div className="prose prose-invert max-w-none whitespace-pre-line text-[#E2E8F0] font-sans-mystic leading-relaxed">
              {result.report}
            </div>
            <Button onClick={() => setResult(null)} variant="ghost" className="mt-6 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="compat-another-btn">
              Read another →
            </Button>
          </div>
        )}
      </main>
    </CosmicBackground>
  );
}
